/**
 * The v6 Director clock (03 §2, §10) from the exact f64 tables (timewarp.bin / timewarp_speed.bin):
 * race_t[k] and speed[k] for every 30 fps video frame k (video time v = k / fps).
 *
 * - raceT(v): linear between frames; exact table values on frame times
 * - videoT(t): inverse (monotone): the earliest video time showing race time t (title hold → 0)
 * - frameForRaceT(t): v6 `searchsorted(race_t, t, 'left')` clamped to [0, n−1]
 * - speed(v), speedLabel(v), phase(v): of the frame at v (v6 speed_factor / speed_label / phase)
 */
import type { Manifest } from '../api.ts';
import type { TimewarpPhase } from '../bundle/manifest.gen.ts';
import { pyFixed, pyRound } from '../state/format.ts';

export type PhaseName = TimewarpPhase['phase'];

/** speed → v6 label: 'PAUSE' if ≤ 0, '%.0fX' when |v − round(v)| < 0.05, else '%.1fX' (Python rounding). */
export function speedLabelFor(v: number): string {
  if (!(v > 0)) return 'PAUSE';
  return Math.abs(v - pyRound(v)) < 0.05 ? `${pyFixed(v, 0)}X` : `${pyFixed(v, 1)}X`;
}

/** A 'suspended' hold (red flag): frames and video seconds of one hold phase; `index` counts the holds in order. */
export interface SuspensionHold { index: number; frame0: number; frame1: number; videoS0: number; videoS1: number }

export class TimeWarp {
  readonly fps: number;
  readonly n: number;
  readonly raceTable: Float64Array;
  readonly speedTable: Float64Array;
  readonly phases: readonly TimewarpPhase[];
  /** per-frame phase index into `phases` */
  private readonly phaseIdx: Uint8Array;
  /** race time of the end hold (= race_t[n−1]) */
  readonly tHold: number;
  /** first frame of the 'lap1' phase (age_frame for events at t ≤ 0) */
  readonly lap1Frame: number;
  readonly duration: number;
  /** the 'suspended' holds in order (empty for races without a red-flag suspension) */
  readonly holds: readonly SuspensionHold[];

  constructor(raceT: Float64Array, speed: Float64Array, phases: readonly TimewarpPhase[], fps = 30) {
    if (raceT.length !== speed.length || raceT.length < 2) throw new Error('timewarp: bad tables');
    this.fps = fps;
    this.n = raceT.length;
    this.raceTable = raceT;
    this.speedTable = speed;
    this.phases = phases;
    this.phaseIdx = new Uint8Array(this.n);
    phases.forEach((p, pi) => {
      for (let k = Math.max(p.frame0, 0); k <= Math.min(p.frame1, this.n - 1); k++) this.phaseIdx[k] = pi;
    });
    this.tHold = raceT[this.n - 1] ?? 0;
    this.lap1Frame = phases.find((p) => p.phase === 'lap1')?.frame0 ?? 0;
    this.duration = (this.n - 1) / fps;
    this.holds = phases.filter((p) => p.phase === 'suspended').map((p, index) => ({
      index, frame0: p.frame0, frame1: p.frame1, videoS0: p.frame0 / fps, videoS1: (p.frame1 + 1) / fps,
    }));
  }

  static fromBundle(b: { manifest: Manifest; warpRaceT: Float64Array; warpSpeed: Float64Array }): TimeWarp {
    return new TimeWarp(b.warpRaceT, b.warpSpeed, b.manifest.timewarp.phases, b.manifest.timewarp.fps);
  }

  /** video time → fractional frame, snapped to the integer within 1e-9 frames (so k/fps hits frame k exactly) */
  frameF(v: number): number {
    const f = Math.min(Math.max((Number.isNaN(v) ? 0 : v) * this.fps, 0), this.n - 1);
    const r = Math.round(f);
    return Math.abs(f - r) < 1e-9 ? r : f;
  }

  /** v6 frame index shown at video time v (floor, clamped) */
  frameAt(v: number): number {
    return Math.floor(this.frameF(v));
  }

  /** race time at video time v (linear between frames, exact on frame times) */
  raceT(v: number): number {
    const f = this.frameF(v);
    const k = Math.floor(f);
    const a = this.raceTable[k] ?? 0;
    if (k >= this.n - 1 || f === k) return a;
    const b = this.raceTable[k + 1] ?? a;
    return a + (f - k) * (b - a);
  }

  /** v6 frame_for_race_t: first k with race_t[k] ≥ t (searchsorted left), clamped to [0, n−1] */
  frameForRaceT(t: number): number {
    const rt = this.raceTable;
    let lo = 0, hi = this.n;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if ((rt[mid] ?? 0) < t) lo = mid + 1;
      else hi = mid;
    }
    return Math.min(Math.max(lo, 0), this.n - 1);
  }

  /** inverse of raceT: the earliest video time at which race time t is shown (monotone; holds → their start) */
  videoT(t: number): number {
    const k = this.frameForRaceT(t);
    const rt = this.raceTable;
    const b = rt[k] ?? 0;
    if (k === 0 || b <= t) return k / this.fps;      // t ≤ race_t[0], exact frame hit, or past the end
    const a = rt[k - 1] ?? 0;
    return (k - 1 + (t - a) / (b - a)) / this.fps;
  }

  /** v6 speed_factor of the frame at video time v (0 in the holds) */
  speed(v: number): number {
    return this.speedTable[this.frameAt(v)] ?? 0;
  }

  speedLabel(v: number): string {
    return this.speedLabelAtFrame(this.frameAt(v));
  }

  /** speed label of video frame k (fixture parity); 'RED FLAG' in a suspension hold */
  speedLabelAtFrame(k: number): string {
    const kk = Math.min(Math.max(Math.trunc(k), 0), this.n - 1);
    if (this.holds.length > 0 && this.phases[this.phaseIdx[kk] ?? 0]?.phase === 'suspended') return 'RED FLAG';
    return speedLabelFor(this.speedTable[kk] ?? 0);
  }

  /** the suspension hold that video time v is inside, or null (HUD / Director: draw the recap card) */
  holdAt(v: number): SuspensionHold | null {
    if (this.holds.length === 0) return null;
    const k = this.frameAt(v);
    return this.holds.find((h) => k >= h.frame0 && k <= h.frame1) ?? null;
  }

  phase(v: number): PhaseName {
    return this.phases[this.phaseIdx[this.frameAt(v)] ?? 0]?.phase ?? 'title';
  }

  /** the video frame an event's presentation age counts from (02 age_frame): t ≤ 0 → first 'lap1' frame */
  ageFrame(tEvent: number): number {
    return tEvent <= 0 ? this.lap1Frame : this.frameForRaceT(tEvent);
  }
}
